/** Landing/marketing copy — absorbs components/landing/tour-content.ts + landing sections. */
export const marketing = {
  // Shared call-to-action / link labels reused across the nav, footer and page.
  cta: {
    signIn: "Sign in",
    signUp: "Sign up",
    getStarted: "Get started",
    createAccount: "Create account",
    contactUs: "Contact us",
    startFreeTrial: "Start free trial",
  },

  // Hero.
  hero: {
    titleLine1: "Every speaker. Every screen.",
    titleLine2: "Run from one place.",
    subtitle:
      "OmniMedia plays the music, announcements, promos and signage in every venue you operate. Set the schedule once. Change it from anywhere.",
    secondaryCta: "See how it works",
    trialNote: "30-day free trial · 1 device · no card required",
  },

  // Alt text for product screenshots / visuals on the marketing pages.
  shots: {
    control: "The OmniMedia control panel",
    scheduling:
      "OmniMedia scheduling timeline with music, ads and announcements laid out across a month",
    scheduleWizard:
      "Building a schedule in OmniMedia with dayparts, audiences and priorities",
    library: "The OmniMedia music library with the licensed catalogue and cover art",
    broadcast: "OmniMedia live-broadcast history across a fleet of locations",
    fleetHealth: "Live fleet status with every player online and now-playing visible",
    dashboard: "The OmniMedia dashboard showing fleet status across every location",
    sites: "Venues and their player fleet listed in one place",
    schedules: "The OmniMedia schedule list across music, ads and screen channels",
    scheduleDetail: "A schedule's entries with day patterns, time windows and priorities",
    signageLibrary: "The screen library with video, web pages and built-in signage widgets",
    ads: "The ads library with promotional spots ready to schedule",
    announcementsLibrary: "The announcements library with recorded and text-to-speech notices",
    catalogue: "The OmniMedia music catalogue browser with genres and cover art",
    reports: "Playback reports showing what played, where and when",
    audiences: "Audience rules targeting schedules at groups of locations",
    playlists: "Playlists and smart stations in the OmniMedia library",
    adRental: "The ad-rental page where a venue lists its screens and speakers as inventory",
    marketplace: "The ad marketplace where advertisers browse venue inventory",
    deviceDetail: "A single player's detail page with status, settings and history",
    playerKiosk: "The OmniMedia player running full-screen on a venue display",
  },

  // Hero product-tour video lightbox.
  video: {
    title: "OmniMedia product tour",
    close: "Close video",
  },

  // Top navigation link labels (landing.tsx NAV_LINKS).
  navLinks: {
    platform: "Platform",
    solutions: "Solutions",
    capabilities: "Capabilities",
    marketplace: "Marketplace",
    device: "Device",
    pricing: "Pricing",
  },

  // Landing nav bar (landing-nav.tsx).
  navbar: {
    openMenu: "Open menu",
    openAccountActions: "Open account actions",
    menu: "Menu",
  },

  // Feature-section highlight chips (landing.tsx).
  highlights: {
    library: {
      curatedCatalog: "Curated catalog",
      uploads: "Uploads",
      playlists: "Playlists",
    },
    scheduling: {
      visualTimeline: "Visual timeline",
      daypart: "Daypart programming",
      fleetSync: "Fleet-wide sync",
    },
    broadcast: {
      instantInterrupt: "Instant interrupt",
      talkToAny: "Talk to any device",
      fromPocket: "Straight from your pocket",
    },
    reliability: {
      deviceHealth: "Live device health",
      nowPlaying: "Now-playing visibility",
      remoteControl: "Remote control",
    },
  },

  // Channel toggle grid (channel-toggle-grid.tsx).
  channelGrid: {
    title: "Four channels. One schedule.",
    subtitle:
      "Music, ads, announcements and signage each run as their own channel. Turn any of them on or off, per device.",
    turnOff: "Turn off {channel} channel",
    turnOn: "Turn on {channel} channel",
  },

  // Ad marketplace section (ad-marketplace-section.tsx).
  adMarketplace: {
    eyebrow: "Ad marketplace",
    title: "Ad space you already own.",
    intro:
      "Your speakers and screens have spot time to spare. Rent it out to advertisers, or book spots in other venues to run your own campaigns. OmniMedia brokers the booking, handles payment and plays the spot. Nothing airs on your floor without your approval.",
    venue: {
      title: "Rent out your spots",
      body: "Open your locations to the marketplace and earn from spot time you already run. You control every spot that plays.",
      points: {
        floorPrice: "Set your own price for each location",
        approve: "Approve or reject every advertiser before it airs",
        payouts: "Automatic Stripe payouts, minus the platform fee",
      },
    },
    advertiser: {
      title: "Advertise in real venues",
      body: "Reach customers where they already shop. Book spot time in listed venues and see exactly where your ad played.",
      points: {
        browse: "Browse and book real, listed venues",
        flatPrice: "A flat price per flight, no per-impression bidding",
        proofOfPlay: "Proof-of-play reporting on every campaign",
      },
    },
    steps: {
      list: "List",
      book: "Book",
      approve: "Approve",
      play: "Play",
      getPaid: "Get paid",
    },
  },

  // Live-broadcast mock scene (broadcast-mock.tsx).
  broadcastMock: {
    holdToTalk: "Hold to talk",
    live: "Live",
    playerLabel: "Store player",
  },

  // Pricing section (pricing-section.tsx).
  pricing: {
    title: "One price. Per device.",
    subtitle:
      "Every feature included. You pay for each player device you add, nothing else. Free for {trialDays} days, no card required.",
    includedTitle: "Everything included",
    includedSubtitle: "One plan. No add-ons, no tiers.",
    estimateTitle: "Estimate your bill",
    estimateSubtitle:
      "Pick your devices and billing period. The total updates as you go.",
    footnote:
      "Start free for {trialDays} days, then subscribe and pay with Stripe inside your workspace.",
    promo:
      "Use code OMWELCOME at checkout for 20% off. The discount applies to every renewal, for as long as you subscribe.",
  },

  // "Why OmniMedia" section (landing.tsx).
  why: {
    heading: "Why OmniMedia",
    items: {
      platform: {
        title: "One platform.",
        body: "Music, screens, announcements and promos, managed from one calm dashboard instead of three tools.",
      },
      reliable: {
        title: "Built to never go quiet.",
        body: "See every device live, fix problems remotely, and speak to any location the moment it matters.",
      },
      pricing: {
        title: "Pricing you can predict.",
        body: "One flat price per player device, every feature included. No tiers, no usage metering, no artificial workspace limits.",
      },
    },
  },

  // Player hardware section (player-device-section.tsx).
  device: {
    eyebrow: "Hardware",
    orderCta: "Order now",
    title: "OmniMedia Player Device",
    body: "A dedicated player for venues that want reliable signage and music without repurposing a PC or tablet.",
    price: "€49.99",
    cardTitle: "OmniMedia Player",
    features: {
      playback: "Audio & video playback",
      output: "4K output",
      wifi: "Wi-Fi",
      bluetooth: "Bluetooth LE",
    },
  },

  // Final call-to-action (landing.tsx).
  finalCta: {
    heading: "Ready when you are.",
    body: "Start free, or reach out if you have questions about your fleet.",
  },

  // Device-frame captions + alt text on /apps. These must describe what the
  // image ACTUALLY shows, not the surface it sits next to — several are
  // honest stand-ins until the native captures land. The mapping is recorded
  // in screenshots/PROVENANCE.md; keep the two in step.
  surfaces: {
    // These currently show the kiosk screen-picker capture (player-kiosk.png),
    // which is what the desktop/TV shells present before a screen is bound.
    // Captions describe THAT, not a playing screen — swap them when the real
    // TV/desktop captures land (screenshots/PROVENANCE.md).
    desktop: {
      caption: "Choosing which screen this player runs",
      alt: "The OmniMedia desktop app asking which screen this player should be",
    },
    tv: {
      caption: "Pairing a screen from the TV box",
      alt: "The OmniMedia player on a venue display, asking which screen to run",
    },
    phone: {
      caption: "The web control room on a phone",
      alt: "The OmniMedia control room open on a phone, showing venues and their players",
    },
    phoneApp: {
      caption: "The mobile companion on a phone",
      alt: "The OmniMedia mobile companion app showing the player list",
    },
    tablet: {
      caption: "The mobile companion on a tablet",
      alt: "The OmniMedia mobile companion running on a tablet",
    },
  },

  // ── Secondary marketing pages ─────────────────────────────────────────────
  // Each block backs one route in MARKETING_PAGES (lib/strings/src/en/meta.ts)
  // and is rendered by components/marketing/marketing-page.tsx. Copy traces to
  // marketing/web/landing-pages.md; the claims rules there are
  // binding (no invented customers/stats, vignettes explicitly illustrative,
  // store-published native apps labelled early access).
  pages: {
    features: {
      nav: {
        music: "Music",
        screens: "Screens",
        announcements: "Announcements",
        promos: "Promos",
      },
      hero: {
        titleLine1: "Everything your venue plays.",
        titleLine2: "One system.",
        subtitle:
          "Music, screens, announcements and promo spots run from one schedule, at one flat price. Set it up once for every location, then change anything from wherever you are.",
      },
      music: {
        title: "Music your venues can play all day.",
        body: "The catalogue is original music, created and owned by TrueSmartTech. We charge no extra music-licensing fees: the right to play it is part of your subscription. Collecting-society obligations vary by country and venue, so check what applies to you. Start from venue presets tuned for real rooms, or build playlists and smart stations of your own.",
        highlights: {
          owned: "Owned catalogue, licence included",
          noFees: "No extra music-licensing fees",
          presets: "Venue presets and smart stations",
        },
      },
      screens: {
        title: "Screens that follow the same schedule.",
        body: "Menus, promos, video and live widgets play on your displays from the same schedule as your audio. What you preview in the console is exactly what plays in the room. Vertical menu boards, entrance screens, TV walls: one library drives them all.",
        highlights: {
          content: "Video, images, web pages and widgets",
          preview: "Preview matches playback",
          hardware: "Runs on TVs, tablets and the player device",
        },
      },
      announcements: {
        title: "Say it in every room at once.",
        body: "Press to talk from the control room and your voice plays where you aimed it: one zone, one location or the whole fleet. Type it instead and OmniMedia reads it out at the times you set.",
        highlights: {
          live: "Live voice from the browser",
          textToSpeech: "Text-to-speech announcements",
          targeting: "Target zones, locations or everything",
        },
      },
      promos: {
        title: "Promos that play themselves.",
        body: "Schedule promotional spots between tracks with day patterns and frequency you control. Every play is logged, so you can show exactly when and where a spot ran.",
        highlights: {
          spots: "Spots between tracks, on schedule",
          patterns: "Day patterns and priorities",
          proof: "Proof of play in reports",
        },
      },
      cta: {
        heading: "See it in your venue.",
        body: "Everything here is included at €49.99 per device per month. Try it free for 30 days on one device, no card required.",
      },
      waitlist: {
        heading: "Ad marketplace: early access",
        body: "We are onboarding venues and advertisers before the marketplace opens. Leave your details and we will get in touch when it goes live in your area. No spam, no obligation.",
        company: "Venue or company",
        email: "Email",
        city: "City",
        locations: "How many locations?",
        role: "I'm interested as…",
        roleVenue: "A venue with screens and speakers to rent out",
        roleAdvertiser: "An advertiser who wants to book in-store slots",
        submit: "Join the waitlist",
        submitting: "Sending…",
        success:
          "Thanks, you're on the list. We'll be in touch when the marketplace opens in your area.",
        error: "Something went wrong. Try again, or email us directly.",
        privacy:
          "We use these details to contact you about the ad marketplace. Product updates only if you tick the box above.",
      },
    },

    apps: {
      nav: {
        web: "Web",
        desktop: "Desktop",
        tv: "TV",
        mobile: "Mobile",
      },
      hero: {
        eyebrow: "One platform, every screen",
        titleLine1: "Runs on what",
        titleLine2: "you already have.",
        subtitle:
          "A browser tab, an old laptop, an Android TV box, a spare phone: anything with a screen or a speaker can become a player. They all answer to the same control room.",
      },
      web: {
        title: "The web player. Nothing to install.",
        body: "Open a browser, sign the screen in once, and it plays. This is the path we recommend for most venues and the one generally available today. Any modern browser, any operating system, with the control room one tab away.",
        highlights: {
          zeroInstall: "Zero install, any modern browser",
          anyOs: "Windows, macOS, Linux, ChromeOS",
          ga: "Generally available today",
        },
      },
      desktop: {
        title: "The desktop app. Keeps playing offline.",
        body: "Install it on a machine in the back office and it caches the schedule and the media ahead of time, so a dropped connection doesn't drop the music. It updates itself and locks into kiosk mode so nobody wanders off into a browser.",
        highlights: {
          offline: "Full offline playback",
          kiosk: "Kiosk lockdown with exit PIN",
          autoUpdate: "Updates itself",
        },
      },
      tv: {
        title: "Android TV. The screen and the player in one box.",
        body: "For signage, the simplest setup is a small Android TV box behind the screen. Pair it by scanning the on-screen code, and it plays video, images, web pages and widgets. It caches ahead for offline and follows the same schedule as the audio.",
        highlights: {
          plugAndPlay: "Pair by scanning the on-screen code",
          signage: "Video, web pages and widgets",
          offline: "Caches ahead for offline playback",
        },
      },
      mobile: {
        title: "The mobile companion. The venue in your pocket.",
        body: "From a phone or tablet, see what every player is doing, change what's playing, and hold to talk live to any location. A spare handset can also act as a player itself, useful for a stockroom or a pop-up.",
        highlights: {
          remote: "Remote control for the whole fleet",
          liveAnnounce: "Hold to talk, live to any location",
          asPlayer: "Or use the phone as a player",
        },
      },
      sync: {
        title: "Rooms that stay in step.",
        body: "Where several players cover one space, like a shop floor and its mezzanine or a bar and its terrace, they play in sync rather than a half-second apart. Clock offset and drift correction are handled on every surface.",
        highlights: {
          multiRoom: "Multi-room synchronized playback",
          driftCorrection: "Clock offset and drift correction",
          everySurface: "On every player surface",
        },
      },
      // C-PLAT-5 summary line (marketing/CLAIMS.md, 2026-09-27): positive-only,
      // names what runs, no release stages (F-19).
      platforms:
        "Runs in any modern browser, on Mac and Windows, on Android TV, and on iPhone and Android phones. The mobile app is on the App Store and Google Play, and the Android TV app is on Google Play.",
      cta: {
        heading: "Start with the tab you already have open.",
        body: "Every surface is the same €49.99 per device per month. Try it free for 30 days on one device, no card required.",
      },
    },

    compare: {
      nav: {
        difference: "The difference",
        channels: "Channels",
        pricing: "Pricing",
        verticals: "Venues",
      },
      hero: {
        eyebrow: "How OmniMedia compares",
        titleLine1: "Four channels.",
        titleLine2: "One license.",
        subtitle:
          "Most venues end up with a music service, a signage service, and no good way to talk to the floor. OmniMedia is all three on one schedule.",
      },
      verifyNote:
        "Competitor pricing and features change often. Verify current details on their own sites before you decide. Everything stated about OmniMedia is what the product does today.",
      difference: {
        title: "One platform instead of three subscriptions.",
        body: "Music-only platforms don't do screens. Signage-only platforms don't do audio, and neither does paging. Running both means two vendors, two schedules and two bills. The promo on the screen still drifts out of step with the one in the speakers.",
        highlights: {
          oneVendor: "One vendor, one bill",
          oneSchedule: "One schedule for audio and screens",
          noAddOns: "No add-on ladder",
        },
      },
      channels: {
        title: "Announcements are a channel, not a surcharge.",
        body: "Live press-to-talk from the browser is included, not a paid module. So are ads, signage and the licensed music catalogue. There is no tier where the useful features live.",
        highlights: {
          live: "Live broadcast included",
          catalogue: "Licensed catalogue included",
          noTiers: "No feature tiers",
        },
      },
      pricing: {
        title: "One number, per device, month to month.",
        body: "€49.99 per device per month, or €499.99 per device per year, about two months free. No plan matrix, no usage metering, no minimum screen count, no annual lock-in.",
        highlights: {
          flat: "€49.99 per device per month",
          noMinimum: "No minimum device count",
          monthToMonth: "Month-to-month",
        },
      },
      verticals: {
        heading: "Built for your kind of venue",
        intro: "The same four channels, tuned to how each kind of room runs.",
        restaurants: {
          title: "Restaurants & cafés",
          body: "Breakfast to close on one schedule: calmer mornings, faster lunch, a dinner room that feels seated. Menu boards follow the kitchen, and the floor hears you the moment you press talk.",
        },
        retail: {
          title: "Retail",
          body: "Head office decides what plays; every store follows. Change a promo across forty shops before the morning shift, and see now-playing for each floor from one screen.",
        },
        gyms: {
          title: "Gyms & studios",
          body: "Every room has its own energy: cardio fast, studio on class time, reception calm. Announce classes live from the front desk.",
        },
        hotels: {
          title: "Hotels",
          body: "Lobby, spa, bar and gym each get the right sound, managed from one tab. Signage follows the day's events without anyone touching a screen.",
        },
      },
      cta: {
        heading: "Compare it on your own floor.",
        body: "30 days free on one device, no card required. If it isn't better than what you have, walk away.",
      },
    },
  },

  // Footer (land9-footer.tsx).
  footer: {
    tagline: "Everything your venues play, run from one place.",
    featuresHeading: "Product",
    companyHeading: "Company",
    accountHeading: "Account",
    links: {
      features: "Features",
      apps: "Apps & players",
      scheduling: "Scheduling",
      compare: "How we compare",
      whyOmnimedia: "Why OmniMedia",
      theProblem: "The problem",
      pricing: "Pricing",
      contact: "Contact",
      privacy: "Privacy",
      terms: "Terms",
    },
    copyright: "{year} OmniMedia · TrueSmartTech OÜ",
    // Accessible names for the footer's social icon row. Each row's URL lives
    // in SOCIAL_PROFILES (en/meta.ts); these are what a screen reader announces
    // and what a hover title shows, since the links render as icons only.
    social: {
      heading: "Follow",
      linkedin: "OmniMedia on LinkedIn",
      instagram: "OmniMedia on Instagram",
      x: "OmniMedia on X",
      facebook: "OmniMedia on Facebook",
    },
  },

  // Public not-found page (pages/marketing/not-found.tsx): any URL outside the
  // marketing site and the app's own routes.
  notFound: {
    eyebrow: "404",
    title: "Page not found",
    body: "There is no page at this address. It may have moved, or the link may be mistyped.",
    home: "Back to the homepage",
    features: "See what OmniMedia does",
  },

  // Product tour prose (tour-content.ts).
  tour: {
    venueExamples:
      "retail stores, restaurants, hotels, gyms, salons, malls, and franchises",
    fleet: {
      title: "Your entire fleet, under control.",
      body: "Organize every player by device and location. See what is playing, what comes next, and which devices are online, from one site to hundreds.",
    },
    surfaces: {
      title: "Manage from anywhere.",
      body: "The control room runs in any modern browser, on a laptop, tablet or phone. Your workspace, schedules and permissions follow you wherever you sign in.",
    },
    channels: {
      title: "Audio and video, together.",
      body: "Four channels, one schedule. No separate tools for music, signage and announcements.",
      items: {
        music: {
          title: "Music",
          desc: "An original catalogue built for venues, with no extra music-licensing fees.",
        },
        ads: {
          title: "Ads",
          desc: "Promo spots between tracks, on the schedule you set.",
        },
        announcements: {
          title: "Announcements",
          desc: "Spoken messages to a single location, a group of devices, or the full fleet.",
        },
        signage: {
          title: "Digital signage",
          desc: "Video, images and widgets on screens, in step with your audio.",
        },
      },
    },
    library: {
      title: "Curated library and your content.",
      body: "Play the licensed catalogue or upload your own media. Start from ready-made playlists, or build smart stations that pick tracks by genre, mood and energy, and keep filling themselves as the catalogue grows.",
    },
    scheduling: {
      title: "Advanced scheduling.",
      body: "Program the day on a visual timeline: morning coffee, evening cocktails, holiday promos. Set it once and every device follows, with overrides when you need them.",
    },
    broadcast: {
      title: "Live broadcast when it matters.",
      body: "Press to talk and your voice interrupts playback in the rooms you choose. Closing time, paging, urgent updates: from your browser to the speaker in seconds.",
    },
    reliability: {
      title: "Reliability you can see.",
      body: "Live heartbeats and now-playing status for every device. You know the moment a location goes quiet, and you can fix it from anywhere. The desktop and Android TV players cache ahead and keep playing when the internet drops.",
    },
  },
  // Opt-in marketing consent checkbox (unchecked by default) shown at every
  // capture point: onboarding trial step, marketplace business step, waitlist.
  // `version` is the proof-of-consent id stored server-side with each answer
  // (MARKETING_CONSENT_WORDING in the API): bump it whenever `label` changes.
  consent: {
    label: "Send me OmniMedia product updates. Unsubscribe anytime.",
    version: "2026-09-27.v1",
  },
} as const;
